import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  input,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  base64Encode,
  DownloadStore,
  FileService,
  interpolate,
  SOFTLINE_FEATURE_DOWNLOAD,
  Store,
} from '@softline/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { File, handleRequestError, SOFTLINE_API_FILE } from '@softline/application';
import { map } from 'rxjs/operators';
import { ImageDialogComponent } from '../../dialogs/image-dialog/image-dialog.component';
import { DevelopmentCheckListStrategy } from '../../dialogs/check-list-dialog/strategies/development-check-list.strategy';
import { AssignCheckListStrategy } from '../../dialogs/check-list-dialog/strategies/assign-check-list.strategy';
import { InternalTestCheckListStrategy } from '../../dialogs/check-list-dialog/strategies/internal-test-check-list.strategy';
import { CustomerTestCheckListStrategy } from '../../dialogs/check-list-dialog/strategies/customer-test-check-list.strategy';
import { AnalyseCheckListStrategy } from '../../dialogs/check-list-dialog/strategies/analyse-check-list.strategy';
import { authorizationFn } from '@softline/auth';
import {
  SOFTLINE_FEATURE_TICKET_CHECKPOINT,
  SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
  SOFTLINE_FEATURE_TICKET_COMMENT,
  SOFTLINE_FEATURE_TICKET_DOCUMENT,
  SOFTLINE_FEATURE_TICKET_TIME_REPORT,
  SOFTLINE_PERMISSION_TICKET_EDITOR,
  SOFTLINE_PERMISSION_TICKET_TIMEREPORT,
  TICKET_ORDER_CONFIRMATION_STEPS,
  TICKET_TEST_CONFIRMATION_STEPS,
  TicketCheckpointStateStore,
  TicketCheckpointStore,
  TicketCommentStore,
  TicketTimeReportStore,
} from '../../ticket.shared';
import { TicketDocumentsStore } from '../../store/document.store';
import { Ticket } from '../../data/ticket.model';
import { askFn, MessageBarService, translator } from '@softline/ui-core';
import { CommonModule } from '@angular/common';
import { MetaDirective, MetaFieldNameDirective, MetaFormatPipe } from '@softline/dynamic';
import { ContactTypePipe } from '../../pipes/contact-type.pipe';
import { TicketService } from '../../services/ticket.service';
import { menuBar, MenuBarButton } from '@softapps/core';
import { RichTextEditorComponent } from '@softapps/core/rich-text';
import { EditTicketDialogComponent } from '../../dialogs/edit-ticket-dialog/edit-ticket-dialog.component';
import { modalFn } from '@softline/ui-core';
import { TicketComment } from '../../data/ticket-comment.model';
import { CreateCommentComponent } from '../../dialogs/create-comment-component/create-comment.component';
import { TicketContactType } from '../../data/contact.model';
import { EditContactsDialogComponent } from '../../dialogs/edit-contacts-dialog/edit-contacts-dialog.component';
import { CreateDocumentComponent } from '../../dialogs/create-document/create-document.component';
import { AssignTicketDialogComponent } from '../../dialogs/assign-ticket-dialog/assign-ticket-dialog.component';
import { CheckListStrategy } from '../../dialogs/check-list-dialog/strategies/check-list.strategy';
import { CheckListDialogComponent } from '../../dialogs/check-list-dialog/check-list-dialog.component';
import { TimeReportDialogComponent } from '../../dialogs/time-report-dialog/time-report-dialog.component';
import { ExportTicketDialogComponent } from '../../dialogs/export-ticket-dialog/export-ticket-dialog.component';
import { NextStepDialogComponent } from '../../dialogs/next-step-dialog/next-step-dialog.component';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { ChipComponent } from '@softline/ui-core';
import { FilesizePipe } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { GroupByPipe } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { InPipe } from '@softline/ui-core';
import { InterpolatePipe } from '@softline/ui-core';
import { IsImagePipe } from '@softline/ui-core';
import { ReplacePipe } from '@softline/ui-core';
import { SecureHtmlPipe } from '@softline/ui-core';
import { ToArrayPipe } from '@softline/ui-core';
import { ToolTipDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { SOFTLINE_CONFIG_TICKET, TicketConfig } from '../../ticket.config';
@Component({
  selector: 'soft-ticket-details',
  imports: [
    CommonModule,
    ContactTypePipe,
    MetaDirective,
    MetaFieldNameDirective,
    MetaFormatPipe,
    MenuBarButton,
    RichTextEditorComponent,
    CdkAccordionModule,
    ChipComponent,
    FilesizePipe,
    FormatPipe,
    GroupByPipe,
    IconComponent,
    InPipe,
    InterpolatePipe,
    IsImagePipe,
    ReplacePipe,
    SecureHtmlPipe,
    ToArrayPipe,
    ToolTipDirective,
    TranslatePipe,
  ],
  templateUrl: './ticket-details.component.html',
  styleUrls: ['./ticket-details.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    DevelopmentCheckListStrategy,
    AssignCheckListStrategy,
    AnalyseCheckListStrategy,
    InternalTestCheckListStrategy,
    CustomerTestCheckListStrategy,
  ],
})
export class TicketDetailsComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private injector = inject(Injector);
  private fileService = inject(FileService);
  private ticketService = inject(TicketService);
  private messageBarService = inject(MessageBarService);
  public orderConfirmationSteps = inject(TICKET_ORDER_CONFIRMATION_STEPS);
  public testConfirmationSteps = inject(TICKET_TEST_CONFIRMATION_STEPS);
  protected ticketConfig: TicketConfig = inject(SOFTLINE_CONFIG_TICKET);

  private t = translator();

  public readonly loading = this.ticketService.isLoading;
  readonly menuBarTemplate = viewChild.required('menuBarTemplate', { read: TemplateRef });
  private readonly modal = modalFn();
  private readonly modalAsk = askFn();

  ticket = input.required<Ticket | null>();

  private subscription?: Subscription;

  private readonly isAuthorized = authorizationFn();
  readonly timeReportPermission = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_TIMEREPORT);
  readonly editorPermission = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDITOR);

  private id$: Observable<string | null> = this.route.paramMap.pipe(
    map((o) => o.get('id'))
  );

  // @Output() load = new EventEmitter<TicketDetailsComponent>();
  // @Output() unload = new EventEmitter<TicketDetailsComponent>();

  private allComments$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET_COMMENT,
    TicketCommentStore.getters.all
  );

  comments$ = combineLatest([this.id$, this.allComments$]).pipe(
    map(([id, allComments]) =>
      allComments.filter((comment) => +comment?.ticketId === +(id ?? -1))
    ),
    map((comments) => comments.sort((a, b) => b.id - a.id))
  );

  private allDocuments$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET_DOCUMENT,
    TicketDocumentsStore.getters.all
  );

  documents$ = combineLatest([this.id$, this.allDocuments$]).pipe(
    map(([id, allDocuments]) =>
      allDocuments.filter((document) => +document.ticketId === +(id ?? -1))
    )
  );

  private defaultStyle = 'primary';
  protected typeColor = computed<string>(() => {
    const type = this.ticket()?.type?.name;
    switch (type) {
      case 'Software Fehler':
        return 'danger';
      case 'Software Wunsch':
        return 'green';
      default:
        return this.defaultStyle;
    }
  });
  protected stepColor = computed<string>(() => {
    const step = this.ticket()?.step.trim();
    if (step && step.startsWith('Intern überprüfen')) {
      return 'primary';
    }
    if (step === 'Geschlossen') {
      return 'success';
    }
    return this.defaultStyle;
  });
  protected stateColor = computed<string>(() => {
    const state = this.ticket()?.state;
    switch (state) {
      case 'new':
        return 'gray';
      case 'open':
        return 'orange';
      case 'completed':
        return 'green';
      case 'released':
        return 'green';
      default:
        return this.defaultStyle;
    }
  });

  protected newDescription: string | null = null;

  constructor() {
    menuBar({
      key: 'customers/softline/ticket',
      title: this.t('Aktionen'),
      template: this.menuBarTemplate,
      align: 'trailing',
      separated: false,
    });
  }

  async ngOnInit(): Promise<void> {
    this.subscription = this.id$.subscribe(async (o) => {
      if (!o) {
        return;
      }
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_COMMENT,
        TicketCommentStore.actions.loadMany,
        { queryParams: { ticketId: +o } }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_DOCUMENT,
        TicketDocumentsStore.actions.loadMany,
        { queryParams: { ticketId: +o } }
      );
    });
    //this.load.emit(this);
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();
    this.subscription = undefined;

    //this.unload.emit(this);
  }

  descriptionChanged(desc: string | null): void {
    this.newDescription = desc;
  }

  async updateDescription(): Promise<void> {
    const id = this.ticket()?.id;
    const ticket = this.ticket();
    if (!id || !this.newDescription) return;
    await this.ticketService.updateTicket(id, {
      ...ticket,
      description: this.newDescription,
    } as Ticket);
    this.newDescription = null;
  }

  async dispatchNextStep(params: { ticketId: number; step: string }): Promise<void> {
    await this.ticketService.setNextStep(params.ticketId, params.step);
  }

  async downloadFile(file: File) {
    const location = { path: SOFTLINE_API_FILE, pathParams: { key: file } };
    const params = { filename: file.name, location };
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_DOWNLOAD,
      DownloadStore.actions.download,
      params
    );
    await this.fileService.openBlob(result.content, result.filename);
  }

  async openImage(file: File): Promise<void> {
    const src = interpolate(SOFTLINE_API_FILE, {
      key: base64Encode(JSON.stringify(file)),
    });
    if (!src) {
      return;
    }
    const result = await this.modal({
      component: ImageDialogComponent,
      title: ``,
      data: {
        src,
        alt: file.name,
      },
      dismiss: true,
    });
  }

  async onEdit(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;
    const result = await this.modal({
      component: EditTicketDialogComponent,
      title: `Ticket bearbeiten`,
      data: {
        ticket: ticket,
      },
      dismiss: true,
    });
    if (!result || result === 'DISMISSED' || result === 'CLOSE') {
      return;
    }
    const updatedTicket = result as Ticket;
    await this.ticketService.updateTicket(updatedTicket.id, updatedTicket);
    //await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), params);
  }

  async onAddComment(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;
    const result = await this.modal({
      component: CreateCommentComponent,
      title: `Kommentar hinzufügen`,
      data: {
        ticket: ticket,
      },
      dismiss: true,
    });

    if (result !== 'DISMISSED') {
      await this.createComment(result);
    }
  }

  async createComment(comment: TicketComment) {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_COMMENT,
        TicketCommentStore.actions.create,
        { entity: comment }
      );
      this.messageBarService.success(this.t('#TICKETS.MESSAGES.COMMENT.CREATE.SUCCESS'));
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async onEditContacts(): Promise<void> {
    const ticket = this.ticket();
    const hasEditorPermission = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDITOR)();
    const contactTypes: TicketContactType[] = [];
    if (hasEditorPermission) {
      for (const item in TicketContactType) {
        contactTypes.push(TicketContactType[item]);
      }
    } else {
      contactTypes.push(TicketContactType.Observer);
    }

    const result = await this.modal({
      component: EditContactsDialogComponent,
      title: `Teilnehmer bearbeiten`,
      data: {
        selectedI: ticket?.contacts,
        contactTypesI: contactTypes,
      },
      dismiss: true,
    });

    if (result !== 'DISMISSED') {
      const updatedTicket = { ...ticket, contacts: result } as Ticket;
      await this.ticketService.updateTicket(updatedTicket.id, updatedTicket);
    }
  }

  async onUploadDocument(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;
    const result = await this.modal({
      id: 'UPLOAD_DOCUMENTS',
      component: CreateDocumentComponent,
      //callback: (documents: NewTicketDocument[]) => ({}),
      data: {
        ticket: ticket,
      },
      dismiss: true,
    });
    if (!result || result === 'DISMISSED' || result === 'CLOSE') {
      return;
    }
  }

  async onAssignTicket(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;
    const result = await this.modal({
      component: AssignTicketDialogComponent,
      title: `Ticket übernehmen`,
      data: {
        ticket: ticket,
      },
      dismiss: true,
    });
    if (result === 'DISMISSED') {
      return;
    }
    await this.ticketService.assignTicket(ticket.id, result.id);
  }

  async onCloseTicket(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;

    const result = await this.modalAsk({
      question: `Möchten sie dieses Ticket wirklich beenden?`,
      dismiss: true,
    });
    if (result !== 'YES') {
      return;
    }
    await this.ticketService.closeTicket(ticket.id);
  }

  async onEditCheckList(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;

    let strategy: CheckListStrategy;
    switch (ticket.checklist) {
      case 'assign':
        strategy = this.injector.get(AssignCheckListStrategy);
        break;
      case 'analyse':
        strategy = this.injector.get(AnalyseCheckListStrategy);
        break;
      case 'development':
        strategy = this.injector.get(DevelopmentCheckListStrategy);
        break;
      case 'internalTest':
        strategy = this.injector.get(InternalTestCheckListStrategy);
        break;
      case 'customerTest':
        strategy = this.injector.get(CustomerTestCheckListStrategy);
        break;
      default:
        this.messageBarService.info('keine Checklisten-Strategie hinterlegt');
        return;
    }

    await this.store.dispatch(
      SOFTLINE_FEATURE_TICKET_CHECKPOINT,
      TicketCheckpointStore.actions.loadMany,
      { queryParams: { ticketId: ticket.id } }
    );
    await this.store.dispatch(
      SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
      TicketCheckpointStateStore.actions.loadMany,
      { queryParams: { ticketId: ticket.id, type: strategy.stage } }
    );

    const result = await this.modal({
      component: CheckListDialogComponent,
      title: `Checkliste`,
      data: {
        strategy,
        ticket: ticket,
      },
      priority: 2,
      dismiss: true,
    });

    if (!result || result === 'DISMISSED' || result === 'CLOSE') {
      return;
    }
  }

  async onEditTimeReport(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;

    this.store.commit(
      SOFTLINE_FEATURE_TICKET_TIME_REPORT,
      TicketTimeReportStore.mutations.clear
    );
    await this.store.dispatch(
      SOFTLINE_FEATURE_TICKET_TIME_REPORT,
      TicketTimeReportStore.actions.loadMany,
      { queryParams: { ticketId: ticket.id } }
    );

    const result = await this.modal({
      component: TimeReportDialogComponent,
      title: `Arbeitszeiterfassung`,
      data: {
        ticket: ticket,
      },
      priority: 2,
      dismiss: true,
    });

    if (!result || result === 'DISMISSED' || result === 'CLOSE') {
      return;
    }

    await this.store.commit(
      SOFTLINE_FEATURE_TICKET_TIME_REPORT,
      TicketTimeReportStore.mutations.clear
    );
  }

  async onExportTicket(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;
    const result = await this.modal({
      component: ExportTicketDialogComponent,
      title: `Exportieren`,
      data: {
        ticket: ticket,
      },
      dismiss: true,
    });
    if (!result || result === 'DISMISSED' || result === 'CLOSE') {
      return;
    }
  }

  async onResetTicket(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;

    const result = await this.modalAsk({
      question: `Möchten sie dieses Ticket wirklich zurücksetzen?`,
      dismiss: true,
    });
    if (result !== 'YES') {
      return;
    }
    this.ticketService.resetTicket(ticket.id);
  }

  async onSetTicketState(): Promise<void> {
    const ticket = this.ticket();
    if (!ticket) return;
    const result = await this.modal({
      component: NextStepDialogComponent,
      title: `Status setzen`,
      data: {
        ticket: ticket,
      },
      dismiss: true,
    });

    if (result !== 'DISMISSED') {
      this.ticketService.setNextStep(result.ticket, result.step);
    } else {
      return;
    }
  }
}
